import { useState, useMemo, useEffect } from 'react'
import { useApiQuery } from '../../../../../utils/api'
import { SprintUserStory } from '@jirax/common'
import type { Sprint, Analysis, UserAnalysis, SprintUser, AddedByResponse, Issue, RouteParams } from '../types'
import { useParams } from 'react-router-dom'

export function useSprintData() {
  const { sprintId, userId } = useParams<RouteParams>()

  // API Query Hooks
  const {
    isLoading: isLoadingSprint,
    error: errorSprint,
    data: sprint
  } = useApiQuery<Sprint>(['sprints', sprintId])

  const {
    isLoading,
    error,
    data: analysis
  } = useApiQuery<Analysis>(['analysis', 'sprints', sprintId], { enabled: !!sprint })

  // Sprint User Story query for timeline view
  const {
    isLoading: isLoadingUserStory,
    error: errorUserStory,
    data: userStoryRaw
  } = useApiQuery<{ success: boolean; data: SprintUserStory }>(
    ['sprint-user-story', sprintId, userId],
    {
      enabled: !!sprintId && !!userId,
      staleTime: 15 * 60 * 1000, // 15 minutes - per React patterns
      retry: 1,
      path: `/sprint-user-story?sprintId=${sprintId}&userId=${userId}`
    }
  )
  const userStory = userStoryRaw?.data

  // Added-by batching state
  const [nonStartingIssueKeys, setNonStartingIssueKeys] = useState<string[]>([])

  // Join keys for the API call
  const keysParam = nonStartingIssueKeys.join(',')

  // Fetch added-by data for all non-starting issues at once
  const {
    data: addedByData,
    isLoading: isLoadingAddedBy
  } = useApiQuery<AddedByResponse[]>(
    `/sprints/${sprintId}/issues/added-by?keys=${keysParam}`,
    {
      enabled: !!sprintId && nonStartingIssueKeys.length > 0,
      // This data is immutable for closed sprints – keep it cached
      staleTime: 24 * 60 * 60 * 1000,   // 24 h
      cacheTime: 48 * 60 * 60 * 1000    // 48 h
    }
  )

  // Create lookup map for O(1) access to added-by data
  const addedByLookup = useMemo<Record<string, AddedByResponse>>(
    () => {
      const map: Record<string, AddedByResponse> = {}
      addedByData?.forEach(r => { map[r.key] = r })
      return map
    },
    [addedByData]
  )

  // useEffect for allIssues computation moved here
  useEffect(() => {
    if (analysis) {
      // Find the user analysis
      const userAnalysis = analysis.byUser.find(user =>
        user.accountId === userId || user.id === userId
      )

      if (userAnalysis) {
        // Get all issues from the user analysis
        const allStartIssues = userAnalysis.start
          .reduce<Issue[][]>((issues, type) => {
            issues.push(type.issues)
            return issues
          }, [])
          .flat(1)

        const allEndIssues = userAnalysis.end
          .reduce<Issue[][]>((issues, type) => {
            issues.push(type.issues)
            return issues
          }, [])
          .flat(1)

        // 🔸 Issues explicitly added during the sprint (may still be in-progress)
        const addedDuringSprint =
          userAnalysis.changes?.addedIssues ?? []

        // Create the combined issues array
        const allIssues: Issue[] = []
        allIssues.push(...allEndIssues)

        for (const issue of allStartIssues) {
          if (!allIssues.find(obj => obj.id === issue.id)) {
            allIssues.push(issue)
          }
        }

        // Include added-during-sprint issues that are NOT already present
        for (const issue of addedDuringSprint) {
          if (!allIssues.find(obj => obj.id === issue.id)) {
            allIssues.push(issue)
          }
        }

        // Mark issues as started with or not
        for (const issue of allIssues) {
          if (allStartIssues.find(obj => obj.id === issue.id)) {
            issue.startedWith = true
          } else {
            issue.startedWith = false
          }
        }

        // Update the keys for the added-by API call
        const keys = allIssues.filter(i => !i.startedWith).map(i => i.key)
        setNonStartingIssueKeys(keys)
      }
    }
  }, [analysis, userId])

  // Enhanced user matching logic with multiple approaches
  let userAnalysis: UserAnalysis | undefined
  let user: SprintUser | undefined

  // First attempt: Direct match by accountId
  userAnalysis = analysis?.byUser.find(u => u.accountId === userId || u.id === userId)
  user = sprint?.users.find(u => u.accountId === userId)

  // Second attempt: Try matching by name (case-insensitive)
  if (!userAnalysis && user) {
    const userDisplayName = user.displayName.toLowerCase().trim()
    userAnalysis = analysis?.byUser.find(
      u => u.name && u.name.toLowerCase().trim() === userDisplayName
    )
  }

  // Third attempt: If we have userAnalysis but no user, try to find user by name
  if (userAnalysis && !user) {
    const analysisUserName = userAnalysis.name.toLowerCase().trim()
    user = sprint?.users.find(
      u => u.displayName && u.displayName.toLowerCase().trim() === analysisUserName
    )
  }

  return {
    sprint,
    analysis,
    userAnalysis,
    user,
    addedByLookup,
    isLoading,
    isLoadingSprint,
    error,
    errorSprint,
    userStory,
    isLoadingUserStory,
    errorUserStory,
    nonStartingIssueKeys,
    setNonStartingIssueKeys,
    isLoadingAddedBy
  }
}
